import { Injectable, inject } from "@angular/core";
import { toLower } from "lodash";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { environment } from "src/environments/environment";
import { loadMessages, locale } from "devextreme/localization";

//@ts-ignore
import itMessages from "devextreme/localization/messages/it.json";
//@ts-ignore
import enMessages from "devextreme/localization/messages/en.json";
//@ts-ignore
import esMessages from "devextreme/localization/messages/es.json";
//@ts-ignore
import frMessages from "devextreme/localization/messages/fr.json";

import { lastValueFrom, map } from "rxjs";
import { TranslocoService } from "@jsverse/transloco";
import { MemberStorageService } from "src/app/services/member-storage.service";
import { dateUtilSetLocale } from "src/app/support/date-util";

import localeIt from "dayjs/locale/it";
import localeEs from "dayjs/locale/es";
import localeFr from "dayjs/locale/fr";
import localeKa from "dayjs/locale/ka";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";

export enum Locales {
  IT = "it-IT",
  EN = "en-GB",
  ES = "es-ES",
  FR = "fr-FR",
  KA = "ka-GE"
}

@Injectable()
export class LocaleService {
  private readonly bootstrapService = inject(BootstrapService);
  private readonly translocoService = inject(TranslocoService);
  private readonly memberStorageService = inject(MemberStorageService);

  private current: Locales = Locales.IT;

  async getLocaleWanted(): Promise<string> {
    const tenant = this.bootstrapService.getCurrentTenant();
    const tenantLocale = tenant?.locale
      ? toLower(tenant.locale.split("-")[0])
      : null;

    const userLanguage = this.detectBrowserLanguage();

    const localeToSet =
      this.memberStorageService.getItem("locale") ||
      tenantLocale ||
      userLanguage ||
      environment.snoots.languages.default;

    return localeToSet;
  }

  async initializeFromUrlAndPreferences(): Promise<string> {
    const localeParam = new URL(location.href).searchParams.get("locale");
    if (
      localeParam &&
      environment.snoots.languages.supported.includes(localeParam)
    ) {
      this.memberStorageService.setItem("locale", localeParam);
    }

    const localeToSet = await this.getLocaleWanted();
    await this.setLocale(localeToSet);

    return localeToSet;
  }

  async setLocale(localeToSet: string): Promise<void> {
    this.configureLocale(localeToSet);

    try {
      await this.activateTranslocoLocale(localeToSet);
    } catch (error: unknown) {
      const fallbackLocale: string = environment.snoots.languages.default;
      if (localeToSet === fallbackLocale) {
        throw error;
      }

      this.configureLocale(fallbackLocale);
      await this.activateTranslocoLocale(fallbackLocale);
    }
  }

  getCurrent(): Locales {
    return this.current;
  }

  private configureLocale(localeToSet: string): void {
    switch (localeToSet) {
      case "ka": {
        loadMessages({
          en: {
            ...enMessages.en,
            "dxScheduler-allDay": "On shift"
          }
        });
        locale("ka-GE");
        dateUtilSetLocale("ka", {
          ...localeKa,
          weekStart: 1
        });

        this.current = Locales.KA;

        break;
      }
      case "it": {
        loadMessages({
          it: {
            ...itMessages.it,
            "dxScheduler-allDay": "In turno"
          }
        });
        locale("it-IT");
        dateUtilSetLocale("it", {
          ...localeIt,
          weekStart: 1
        });

        this.current = Locales.IT;
        break;
      }
      case "en": {
        loadMessages({
          en: {
            ...enMessages.en,
            "dxScheduler-allDay": "On shift"
          }
        });
        locale("en");

        this.current = Locales.EN;
        break;
      }
      case "es": {
        loadMessages({
          es: {
            ...esMessages.es,
            "dxScheduler-allDay": "En turno"
          }
        });
        locale("es");
        dateUtilSetLocale("es", localeEs);

        this.current = Locales.ES;
        break;
      }
      case "fr": {
        loadMessages({
          fr: {
            ...frMessages.fr,
            "dxScheduler-allDay": "En turno"
          }
        });
        locale("fr");
        dateUtilSetLocale("fr", localeFr);

        this.current = Locales.FR;
        break;
      }
    }
  }

  private detectBrowserLanguage(): string | null {
    const supported: string[] = environment.snoots.languages.supported;
    const browserLocales: readonly string[] =
      navigator.languages.length > 0
        ? navigator.languages
        : [navigator.language];

    const region: string | undefined = this.getBrowserRegion();
    if (region === "IT" && supported.includes("it")) {
      return "it";
    }

    const language: string | undefined = browserLocales
      .map((lang) => toLower(lang.split("-")[0]))
      .filter((lang) => !!lang)
      .find((lang) => supported.includes(lang));

    return language ?? null;
  }

  private getBrowserRegion(): string | undefined {
    try {
      const primaryLocale: string =
        navigator.languages[0] ?? navigator.language ?? "";
      const regionFromPrimary: string | undefined = primaryLocale
        .split("-")[1]
        ?.toUpperCase();

      if (regionFromPrimary) {
        return regionFromPrimary;
      }

      return Intl.DateTimeFormat()
        .resolvedOptions()
        .locale.split("-")[1]
        ?.toUpperCase();
    } catch {
      return undefined;
    }
  }

  private async activateTranslocoLocale(localeToSet: string): Promise<void> {
    await lastValueFrom(
      this.translocoService.load(localeToSet).pipe(
        map(() => {
          this.translocoService.setActiveLang(localeToSet);
          return true;
        })
      )
    );
  }
}
